import { z } from 'zod';

import { US_STATE_NAMES } from './client-profile.us-states';

export const MAX_INPUT_FIELD_LENGTH = 256;

export const getErrorMessage = (fieldName: string) =>
    `${fieldName} must be at most ${MAX_INPUT_FIELD_LENGTH} characters`;

const DATE_OF_BIRTH_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;
const DATE_OF_BIRTH_MESSAGE = 'Enter date as MM/DD/YYYY';
const DATE_OF_BIRTH_FUTURE_MESSAGE = 'Date of birth cannot be in the future';
const DATE_OF_BIRTH_TOO_OLD_MESSAGE = 'Please check the year of birth';
const MAX_AGE_YEARS = 120;

const PHONE_DIGIT_COUNT = 10;
const PHONE_MESSAGE = 'Enter valid 10-digit phone number';

/**
 * `MM/DD/YYYY` in, a UTC-midnight `Date` out. The round-trip rejects dates that match the pattern
 * but do not exist — `02/31/2020` rolls over to March 2nd, so it comes back as a different day.
 *
 * A date after today is rejected, where today is the current UTC day. Comparing against `new Date()`
 * would make the browser (local zone) and the Lambda (UTC) disagree for hours each day, and would
 * reject a newborn's date of birth entered as "today" west of UTC. At the boundary this errs toward
 * accepting.
 *
 * A date more than 120 years before today is rejected on the same UTC basis; exactly 120 years ago
 * is accepted. When today is 29 February and the year 120 back has no such day, the cutoff clamps
 * to 28 February rather than rolling over to 1 March, so the boundary still errs toward accepting.
 */
export const dateOfBirthSchema = z
    .string()
    .trim()
    .transform((value, ctx) => {
        const parts = DATE_OF_BIRTH_PATTERN.exec(value);

        if (!parts) {
            ctx.addIssue({ code: 'custom', message: DATE_OF_BIRTH_MESSAGE });
            return z.NEVER;
        }

        const year = Number(parts[3]);
        const month = Number(parts[1]);
        const day = Number(parts[2]);
        const parsed = new Date(Date.UTC(year, month - 1, day));

        if (
            parsed.getUTCFullYear() !== year ||
            parsed.getUTCMonth() !== month - 1 ||
            parsed.getUTCDate() !== day
        ) {
            ctx.addIssue({ code: 'custom', message: DATE_OF_BIRTH_MESSAGE });
            return z.NEVER;
        }

        const now = new Date();
        const todayYear = now.getUTCFullYear();
        const todayMonth = now.getUTCMonth();
        const todayDay = now.getUTCDate();
        const todayUtc = Date.UTC(todayYear, todayMonth, todayDay);

        if (parsed.getTime() > todayUtc) {
            ctx.addIssue({ code: 'custom', message: DATE_OF_BIRTH_FUTURE_MESSAGE });
            return z.NEVER;
        }

        const cutoffYear = todayYear - MAX_AGE_YEARS;
        const lastDayOfCutoffMonth = new Date(Date.UTC(cutoffYear, todayMonth + 1, 0)).getUTCDate();
        const oldestUtc = Date.UTC(
            cutoffYear,
            todayMonth,
            Math.min(todayDay, lastDayOfCutoffMonth),
        );

        if (parsed.getTime() < oldestUtc) {
            ctx.addIssue({ code: 'custom', message: DATE_OF_BIRTH_TOO_OLD_MESSAGE });
            return z.NEVER;
        }

        return parsed;
    });

/**
 * Any formatting in, ten bare digits out, so `+1 (309) 249-2342` and `309.249.2342` normalise to
 * the same stored value. A leading country-code `1` is dropped rather than counted as an 11th digit.
 */
export const usPhoneSchema = z
    .string()
    .transform((value) => {
        const digits = value.replace(/\D/g, '');

        return digits.length === PHONE_DIGIT_COUNT + 1 && digits.startsWith('1')
            ? digits.slice(1)
            : digits;
    })
    .refine((digits) => digits.length === PHONE_DIGIT_COUNT, PHONE_MESSAGE);

/**
 * Accepts `''` alongside `null` and a valid value, normalising the empty string to `null`. A masked
 * input emits `''` for a cleared field and never `null`, so without this a cleared optional field
 * could not be saved at all.
 *
 * `zip` is deliberately not wrapped: `Address.zip` is a non-nullable string, so `''` is the correct
 * stored value there.
 */
export const emptyToNull = <T extends z.ZodType<unknown, string>>(schema: T) =>
    z
        .string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .pipe(schema.nullable());

// Trims first, so a whitespace-only value is no email rather than an invalid one. The length cap
// keeps an oversized value from reaching the unique index on `persons.email_primary`.
export const optionalEmailSchema = z
    .string()
    .trim()
    .max(MAX_INPUT_FIELD_LENGTH, `Email must be at most ${MAX_INPUT_FIELD_LENGTH} characters`)
    .nullable()
    .transform((value) => (value === '' ? null : value))
    .pipe(z.email('Enter a valid email address (e.g., name@example.com)').nullable());

export const usAddressSchema = z.object({
    street1: z.string().trim().max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Street address')),
    street2: z.string().trim().max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('Apt, suite or floor')),
    city: z.string().trim().max(MAX_INPUT_FIELD_LENGTH, getErrorMessage('City')),
    // Full name, not the code (as practice stores it); '' is the form's "no state selected".
    state: z.enum(US_STATE_NAMES).or(z.literal('')),
    zip: z
        .string()
        .trim()
        .regex(/^\d{5}$/, 'Enter a 5-digit ZIP')
        .or(z.literal('')),
});
